import React, { Component } from 'react'
import { withRouter, Redirect } from 'react-router'
import ReactRouterPropTypes from 'react-router-prop-types'
import { PropTypes as MobxPropTypes, inject, observer } from 'mobx-react'
import { observable, action, makeObservable } from 'mobx'
import { isNil, path } from 'ramda'
import ReCAPTCHA from 'react-google-recaptcha'
import { Helmet } from 'react-helmet'
import Modal from 'react-responsive-modal'

import { html2jsx } from 'Lib/parser'
import Link from 'Components/link'
import { login, trackLogin } from 'Api/endpoints/auth'
import Spinner from 'Components/spinner'
import BaseLayout from 'Containers/Sites/Base/Layouts/base'

const recaptchaRef = React.createRef()

const triggerReports = ({
  snaid, email, firstName, lastName
}) => {
  // For BSS to snowflake tracking
  if (window.bsspx) {
    try {
      window.bsspx.sendData({
        snaid: snaid,
        email,
        name: `${firstName} ${lastName}`,
        first_name: firstName,
        last_name: lastName
      })
      console.log('BSS tracking sent')
    } catch (e) {
      console.warn('BSS tracking Error')
      console.log(e)
    }
  } else {
    console.log('BSS tracking missing')
  }
}

@inject('store')
@observer
class Login extends Component {
  @observable BaseLayout = null;

  @observable username = '';

  @observable password = '';

  @observable rememberCheck = true;

  @observable errorMessage = 'Login Failed';

  @observable showErrorMessage = false;

  @observable isLoggingIn = false;

  @observable recaptchaValue = '';

  @observable modalIsOpen = false;

  static propTypes = {
    store: MobxPropTypes.objectOrObservableObject,
    history: ReactRouterPropTypes.history.isRequired
  };

  constructor (props) {
    super(props)
    makeObservable(this)
  }

  @action.bound
  handleUpdateRecaptchaValue (e) {
    this.recaptchaValue = e
  }

  @action.bound
  handleUpdateUsername (e) {
    this.username = e.target.value
    // this.showErrorMessage = false;
  }

  @action.bound
  handleUpdatePassword (e) {
    this.password = e.target.value
    // this.showErrorMessage = false;
  }

  @action.bound
  toggleRememberCheck () {
    this.rememberCheck = !this.rememberCheck
  }

  @action.bound
  async handleDoLogin (e) {
    this.isLoggingIn = true
    e.preventDefault()

    const { username, password } = this
    const { sitecode, brandId } = this.props.store.site

    const { error, value = {} } = await login(username, password, sitecode, this.recaptchaValue)
    const { token } = value

    if (isNil(error)) {
      await this.props.store.user.afterLogin(value)
      this.props.store.user.setUsernameDuringLogin(username)

      if (this.props.store.user.decodedToken) {
        const {
          customerNumber, customerEmail, customerFirstName, customerLastName
        } = this.props.store.user.decodedToken

        if (window.app_env === 'live') {
          window.dataLayer.push({
            event: 'login',
            snaid: customerNumber,
            brandid: brandId,
            device: 'browser'
          })
        }

        trackLogin(sitecode, token)

        triggerReports({
          snaid: customerNumber,
          email: customerEmail,
          firstName: customerFirstName,
          lastName: customerLastName
        })
      } else {
        console.log('No decode token')
      }

      this.redirectMember()
    } else {
      try {
        const err = JSON.parse(error)
        const message = path(['response', 'data', 'message'], err)

        if (message === 'legacy-only') {
          this.toggleModal()
        } else {
          this.errorMessage = !message ? 'Login Failed' : message
        }
      } catch (err) {
        console.log(err)
      }

      this.recaptchaValue = ''
      recaptchaRef.current.reset()
      this.showErrorMessage = true
      this.username = ''
      this.password = ''
    }

    this.isLoggingIn = false
  }

  @action.bound
  redirectMember () {
    const location = this.props.store.ui.destinationAfterLogin || '/my-subscriptions'
    this.props.history.push(location)
  }

  @action.bound
  toggleModal () {
    this.modalIsOpen = !this.modalIsOpen
  }

  render () {
    const { captchaKey } = this.props.store.site
    const { shouldDisplayLoginMsg, isLoggedIn } = this.props.store.ui
    const {
      siteDetails: {
        messages: { loginMsg },
        phone, mail
      }
    } = this.props.store.site
    const { tokenExistsButIsExpired, logout } = this.props.store.user

    if (tokenExistsButIsExpired) {
      logout()
    }

    if (isLoggedIn) {
      return <Redirect to='/my-subscriptions' />
    }

    return (
      <BaseLayout>
        <Helmet>
          <title>Login</title>
        </Helmet>
        <div className='container' id='__page_login'>
          {shouldDisplayLoginMsg && (
            <aside className='row loginMsgContainer'>
              <div className='col-12 loginMsgWrapper'>
                {html2jsx(loginMsg.content)}
              </div>
            </aside>
          )}
          <div className='row'>
            <div className='col-md-12 actions'>
              <section>
                <h4>Subscriber Login</h4>

                <form onSubmit={this.handleDoLogin}>
                  <div className='form-group'>
                    <input
                      type='text'
                      value={this.username}
                      onChange={this.handleUpdateUsername}
                      placeholder='Email/Username'
                    />
                  </div>
                  <div className='form-group'>
                    <input
                      type='password'
                      value={this.password}
                      onChange={this.handleUpdatePassword}
                      placeholder='Password'
                    />
                  </div>
                  <div className='form-group recaptchaWrapper'>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={captchaKey}
                      onChange={this.handleUpdateRecaptchaValue}
                    />
                  </div>
                  {!this.isLoggingIn && (
                    <button type='submit' className='btn'>
                      Log In
                    </button>
                  )}
                  {this.isLoggingIn && (
                    <button type='submit' className='btn' disabled>
                      <Spinner />
                    </button>
                  )}
                  {this.showErrorMessage && (
                    <div className='invalid-feedback'>{this.errorMessage}</div>
                  )}
                  <div className='row lastLine'>
                    <div className='col-12 passReminder'>
                      <Link to='/reset'>Forgot username or password?</Link>
                    </div>
                  </div>
                </form>
              </section>
            </div>
          </div>

          <div className='row smallLetters'>
            <p>
              You must have a subscription to log in. If you need assistance,
              contact customer service at{' '} <a href={`mailto:${mail.link}`}> {mail.services} </a>{' '}
              or call{' '} <a href={phone.link}>{phone.value}</a>.
            </p>
          </div>
        </div>

        <Modal
          open={this.modalIsOpen}
          onClose={() => { this.modalIsOpen = !this.modalIsOpen }}
          classNames={{
            overlay: 'r-modal-login-overlay',
            modal: 'r-modal-login-modal',
            closeButton: 'r-modal-login-close'
          }}
        >
          <div>
            <h4>A Legacy product is required to access this site. Please access your subscription from one of the appropriate sites below:</h4>
            <ul>
              <li>
                <a target='_blank' rel='noopener noreferrer' href='https://members.palmbeachgroup.com/login/'>Palm Beach Group</a>
              </li>
              <li>
                <a target='_blank' rel='noopener noreferrer' href='https://members.bonnerandpartners.com/login/'>Bonner and Partners</a>
              </li>
              <li>
                <a target='_blank' rel='noopener noreferrer' href='https://members.jeffclarktrader.com/login/'>Jeff Clark Trader</a>
              </li>
            </ul>
          </div>
        </Modal>
      </BaseLayout>
    )
  }
}

export default withRouter(Login)
